import type { SignupSchool, SignupSport } from '../../types/signup';

/**
 * Display helpers for self-serve signup. Pure, so every page quotes a price and names a school the
 * same way. Nothing here decides a price: it only formats the one the server sent.
 */

const currencyFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Cached per currency: constructing an `Intl.NumberFormat` costs far more than calling `format` on
 * one (see "Reuse the cached Intl.DateTimeFormat" in web/CLAUDE.md).
 */
const currencyFormatter = (currency: string, wholeUnits: boolean): Intl.NumberFormat => {
  const key = `${currency}|${wholeUnits}`;
  let formatter = currencyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: wholeUnits ? 0 : 2,
      maximumFractionDigits: wholeUnits ? 0 : 2,
    });
    currencyFormatters.set(key, formatter);
  }
  return formatter;
};

/** "$4,500". Whole dollars drop the cents; anything else keeps them. */
export const formatPrice = (cents: number, currency: string): string => {
  const wholeUnits = cents % 100 === 0;
  try {
    return currencyFormatter(currency || 'usd', wholeUnits).format(cents / 100);
  } catch {
    // An unrecognised currency code throws a RangeError. Show the number rather than nothing.
    return `${(cents / 100).toFixed(wholeUnits ? 0 : 2)} ${currency.toUpperCase()}`;
  }
};

/** "$4,500 / year" */
export const formatAnnualPrice = (cents: number, currency: string): string =>
  `${formatPrice(cents, currency)} / year`;

/** "Ohio · D1 · Big Ten", skipping whatever the catalog does not know. */
export const describeSchool = (school: SignupSchool): string =>
  [school.state, school.level, school.conference].filter(Boolean).join(' · ');

export const sportLabel = (sport: SignupSport | string | null | undefined): string =>
  sport === 'softball' ? 'Softball' : sport === 'baseball' ? 'Baseball' : '';
